import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {type CustomerInquiryResponseBody} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {useVehicleCatalogue, useVehicleMake, useVehicleModel} from '@dms/shared';

import {type Nullish, parseDate} from 'shared/web';

function formatProduction(
  realProductionMonth: string | Nullish,
  realProductionYear: string | Nullish
) {
  return match([realProductionMonth, realProductionYear])
    .with([Pattern.string, Pattern.string], ([month, year]) => `${month}/${year}`)
    .with([Pattern.nullish, Pattern.string], ([, year]) => year)
    .with([Pattern.string, Pattern.nullish], ([month]) => month)
    .otherwise(() => null);
}

export function useRequestSpecification(customerInquiry: CustomerInquiryResponseBody | Nullish) {
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  const vehicleDetails = customerInquiry?.vehicleDetails;
  const vehicleInformation = customerInquiry?.vehicleInformation;
  const makeCodes = isNotNil(vehicleDetails?.make) ? [vehicleDetails.make] : null;

  const [vehicleUtils] = useVehicleCatalogue(vehicleDetails?.vehicleType);
  const [{makeOptions}] = useVehicleMake(vehicleDetails?.vehicleType);
  const [{modelOptions}] = useVehicleModel(vehicleDetails?.vehicleType, makeCodes);

  const vehicleType = vehicleUtils.getVehicleType(vehicleDetails?.vehicleType) ?? null;
  const make = makeOptions?.find((option) => option.value === vehicleDetails?.make)?.label ?? null;
  const modelFamily =
    modelOptions?.find((option) => option.value === vehicleDetails?.modelFamily)?.label ?? null;
  const firstRegistration = match(vehicleDetails?.firstRegistration)
    .with(Pattern.string, (value) => formatDateTime('dateShort', parseDate(value)))
    .otherwise(() => null);
  const production = formatProduction(
    vehicleDetails?.realProductionMonth,
    vehicleDetails?.realProductionYear
  );
  const currentMileage = match(vehicleInformation?.currentMileage)
    .with(Pattern.number, (mileage) =>
      match(formatNumber(mileage, 0))
        .with(
          Pattern.string,
          (formattedMileage) => `${formattedMileage} ${i18n.t('general.metric.km')}`
        )
        .otherwise(() => null)
    )
    .otherwise(() => null);

  return {
    vehicleType,
    make,
    modelFamily,
    trim: isNil(vehicleDetails?.trim) ? null : vehicleDetails.trim,
    variant: isNil(vehicleDetails?.variant) ? null : vehicleDetails.variant,
    licensePlate: isNil(vehicleDetails?.licensePlate) ? null : vehicleDetails.licensePlate,
    vin: isNil(vehicleDetails?.vin) ? null : vehicleDetails.vin,
    firstRegistration,
    production,
    currentMileage,
  };
}
